import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import {
  AppCard,
  AppNoInfo,
  AppNotificationAlert,
} from "@/layout/AppComponents";
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalOffice/ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsCard';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "emergencyAndPotentialEnvironmentalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "emergencyAndPotentialEnvironmentalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="office"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_office_emergency_and_potential_environmental_aspects" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="office.environmental.aspects.identification.evaluation"
              />
            </Title>
            <Subtitle>
              <Translator
                file="module-comsaEnvironmental"
                name="office.emergency.and.potential.environmental.aspects"
              />
            </Subtitle>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <AppCard className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.liquid.waste"
                    />
                  </h6>
                </AppCard>
                <ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsCard
                  values={values.fireExtinguishing}
                  setFieldValue={(n, v) =>
                    setFieldValue(`fireExtinguishing.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="fire.extinguishing"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="sewer.pollution"
                    />
                  }
                />
                <AppCard
                  className="mb-2"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="aspect"
                    />
                  )}
                >
                  <h6 className="mb-0">
                    <Translator
                      file="module-comsaEnvironmental"
                      name="emissions.atmosphere"
                    />
                  </h6>
                </AppCard>
                <ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsCard
                  values={values.emissionsFire}
                  setFieldValue={(n, v) =>
                    setFieldValue(`emissionsFire.${n}`, v)
                  }
                  motive={
                    <Translator file="module-comsaEnvironmental" name="fire" />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="air.pollution"
                    />
                  }
                />
                <AppCard className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.bar.non.hazardous.waste"
                    />
                  </h6>
                </AppCard>
                <ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsCard
                  values={values.dangerousWastesFire}
                  setFieldValue={(n, v) =>
                    setFieldValue(`dangerousWastesFire.${n}`, v)
                  }
                  motive={
                    <Translator file="module-comsaEnvironmental" name="fire" />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <AppCard className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.bar.hazardous.waste"
                    />
                  </h6>
                </AppCard>
                <ModuleComsaEnvironmentalOfficeEmergencyAndPotentialEnvironmentalAspectsCard
                  values={values.noDangerousWastesFire}
                  setFieldValue={(n, v) =>
                    setFieldValue(`noDangerousWastesFire.${n}`, v)
                  }
                  motive={
                    <Translator file="module-comsaEnvironmental" name="fire" />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="soil.pollution"
                    />
                  }
                />
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h4`
  text-align: center;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
